/**
 * Resolução de tier por MEDIÇÃO (PERFORMANCE M1).
 *
 * NEVER sniffe user agent: envelhece, mente, e quebra em modelo novo.
 * Sinais de hardware são só palpite inicial; a medição decide.
 */
import { TIER_KEYS, tierSpec, type TierKey } from '@game/gamedata/perf';

/** Sinais de hardware. Todos opcionais e todos secundários à medição. */
export interface TierHints {
  hardwareConcurrency?: number;
  deviceMemoryGb?: number;
  maxTextureSize?: number;
}

/** Armazenamento injetável: mantém isto testável sem browser. */
export interface TierStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

const STORAGE_KEY = 'perf.tier.v1';

function isTierKey(value: string): value is TierKey {
  return (TIER_KEYS as readonly string[]).includes(value);
}

/**
 * Palpite inicial, deliberadamente CONSERVADOR.
 * Subir de tier depois é imperceptível; começar alto e travar não é.
 */
export function initialTierGuess(hints: TierHints = {}): TierKey {
  const cores = hints.hardwareConcurrency ?? 4;
  const memory = hints.deviceMemoryGb ?? 4;

  if (cores >= 8 && memory >= 8) return 'mid';
  if (cores <= 4 && memory <= 4) return 'low';
  return 'low';
}

function indexOfTier(key: TierKey): number {
  return TIER_KEYS.indexOf(key);
}

/** Limite de frame time acima do qual o tier atual é insustentável. */
export function frameBudgetMs(key: TierKey): number {
  return 1000 / tierSpec(key).targetFps;
}

export type TierDecision =
  | { action: 'keep'; tier: TierKey }
  | { action: 'demote'; tier: TierKey; reason: string }
  | { action: 'promote'; tier: TierKey; reason: string };

/**
 * Decide promoção ou rebaixamento a partir do p95 medido.
 *
 * A margem assimétrica é intencional: rebaixa rápido (o jogador está sofrendo agora),
 * promove com folga grande (para não ficar oscilando entre dois tiers).
 */
export function decideTier(current: TierKey, p95FrameMs: number): TierDecision {
  const budget = frameBudgetMs(current);
  const index = indexOfTier(current);

  // Rebaixa: estourou o orçamento do próprio tier com folga de 20%.
  if (p95FrameMs > budget * 1.2) {
    const lower = TIER_KEYS[index - 1];
    if (lower !== undefined) {
      return {
        action: 'demote',
        tier: lower,
        reason: `p95 ${p95FrameMs.toFixed(1)}ms acima do orçamento ${budget.toFixed(1)}ms`,
      };
    }
    return { action: 'keep', tier: current };
  }

  // Promove: só se sobrar 35% de folga, para não oscilar.
  const higher = TIER_KEYS[index + 1];
  if (higher !== undefined) {
    const higherBudget = frameBudgetMs(higher);
    if (p95FrameMs < higherBudget * 0.65) {
      return {
        action: 'promote',
        tier: higher,
        reason: `p95 ${p95FrameMs.toFixed(1)}ms com folga para ${higher}`,
      };
    }
  }

  return { action: 'keep', tier: current };
}

/** Tier resolvido em sessão anterior. Segundo boot já começa certo. */
export function loadPersistedTier(store: TierStore): TierKey | undefined {
  const raw = store.get(STORAGE_KEY);
  if (raw === null) return undefined;
  return isTierKey(raw) ? raw : undefined;
}

export function persistTier(store: TierStore, key: TierKey): void {
  store.set(STORAGE_KEY, key);
}

/** Tier de partida: o persistido, senão o palpite conservador. */
export function startingTier(store: TierStore, hints: TierHints = {}): TierKey {
  return loadPersistedTier(store) ?? initialTierGuess(hints);
}
